import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  IconButton,
  AppBar,
  Toolbar,
  SwipeableDrawer,
  Chip,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Person as TeacherIcon,
  AdminPanelSettings as CoordIcon,
} from '@mui/icons-material';

// Feature components
import KnowledgeMapManager from './KnowledgeMapManager.tsx';
import CurriculumPlanner from './CurriculumPipeline.tsx';
import WeekWiseStudyPlan from './WeekWiseStudyPlan.tsx';
import InteractiveClassroom from './InteractiveClassroom.tsx';
import TeacherGuidelines from './TeacherGuidelines.tsx';
import StudentDashboard from './StudentDashboard.tsx';
import LogoutIcon from '@mui/icons-material/Logout';
// Role system - make sure this path is correct
import { RoleProvider, useRole, UserRole } from './syllabus/RoleContext.tsx';
import DynamicFlow from '../DynamicFlow.tsx';
import { ConstantActions } from '../../../Services/_actions/constant.actions.js';

const drawerWidth = 'auto';

// Navigation state interface
interface NavigationState {
  board: string;
  class: string;
  subject: string;
}

const menuItems = [
  { id: 'knowledgeMap', label: 'Knowledge Map', fullLabel: 'Knowledge Map', icon: <MapIcon />, component: DynamicFlow },
  { id: 'unitwisesyllabus', label: 'Unit Wise Syllabus', fullLabel: 'Unit Wise Syllabus', icon: <MapIcon />, component: KnowledgeMapManager },
  { id: 'curriculum', label: 'Curriculum', fullLabel: 'Curriculum Planner', icon: <TimelineIcon />, component: CurriculumPlanner },
  { id: 'studyPlan', label: 'Study Plan', fullLabel: 'Week-Wise Study Plan', icon: <AssignmentIcon />, component: WeekWiseStudyPlan },
  { id: 'classroom', label: 'Classroom', fullLabel: 'Interactive Classroom', icon: <SchoolIcon />, component: InteractiveClassroom },
  { id: 'teacherGuidelines', label: 'Guidelines', fullLabel: 'Teacher Guidelines', icon: <PeopleIcon />, component: TeacherGuidelines },
  { id: 'studentDashboard', label: 'Students', fullLabel: 'Student Dashboard', icon: <DashboardIcon />, component: StudentDashboard },
];

// ── Role switcher component ───────────────────────────────────────────────
// function RoleSwitcherInline({ compact = false }: { compact?: boolean }) {
//   const { role, setRole } = useRole();

//   if (compact) {
//     return (
//       <Chip
//         icon={role === 'coordinator' ? <CoordIcon sx={{ fontSize: 14 }} /> : <TeacherIcon sx={{ fontSize: 14 }} />}
//         label={role === 'coordinator' ? 'Coordinator' : 'Teacher'}
//         size="small"
//         onClick={() => setRole(role === 'teacher' ? 'coordinator' : 'teacher')}
//         sx={{
//           bgcolor: role === 'coordinator' ? '#E8F5E9' : '#E3F2FD',
//           color: role === 'coordinator' ? '#1B5E20' : '#0D47A1',
//           fontWeight: 600,
//           fontSize: 10,
//           cursor: 'pointer',
//           border: '1px solid',
//           borderColor: role === 'coordinator' ? '#A5D6A7' : '#90CAF9',
//           '& .MuiChip-icon': {
//             color: role === 'coordinator' ? '#1B5E20' : '#0D47A1',
//           },
//         }}
//       />
//     );
//   }

//   return (
//     <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid #E0E0E0' }}>
//       <Typography variant="caption" sx={{ color: '#9E9E9E', display: 'block', mb: 0.75, fontWeight: 500, letterSpacing: '.04em', textTransform: 'uppercase', fontSize: 10 }}>
//         Active role
//       </Typography>
//       <ToggleButtonGroup
//         value={role}
//         exclusive
//         size="small"
//         onChange={(_e, val: UserRole | null) => { if (val) setRole(val); }}
//         fullWidth
//         sx={{
//           '& .MuiToggleButton-root': {
//             fontSize: 11,
//             py: 0.6,
//             textTransform: 'none',
//             fontWeight: 500,
//             gap: 0.5,
//             border: '1px solid #E0E0E0',
//             color: '#757575',
//             '&.Mui-selected': {
//               bgcolor: '#E3F2FD',
//               color: '#1565C0',
//               borderColor: '#90CAF9',
//               '&:hover': { bgcolor: '#BBDEFB' },
//             },
//           },
//         }}
//       >
//         <Tooltip title="Build and submit curriculum plans" placement="bottom">
//           <ToggleButton value="teacher">
//             <TeacherIcon sx={{ fontSize: 14 }} /> Teacher
//           </ToggleButton>
//         </Tooltip>
//         <Tooltip title="Review and approve plans" placement="bottom">
//           <ToggleButton value="coordinator">
//             <CoordIcon sx={{ fontSize: 14 }} /> Coordinator
//           </ToggleButton>
//         </Tooltip>
//       </ToggleButtonGroup>
//     </Box>
//   );
// }

// ─── Knowledge Map Wrapper ────────────────────────────────────────────────
function KnowledgeMapWrapper({ onNavigateToCurriculum, initialBoard, initialClass, initialSubject }: {
  onNavigateToCurriculum: (board: string, classVal: string, subject: string) => void;
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
}) {
  return (
    <KnowledgeMapManager
      initialBoard={initialBoard}
      initialClass={initialClass}
      initialSubject={initialSubject}
      onNavigateToCurriculum={onNavigateToCurriculum}
    />
  );
}

// ─── Curriculum Wrapper ───────────────────────────────────────────────────
function CurriculumWrapper({
  onBackToKnowledgeMap,
  onProceedToStudyPlan,
  initialBoard,
  initialClass,
  initialSubject,
}: {
  onBackToKnowledgeMap: (board: string, classVal: string, subject: string) => void;
  onProceedToStudyPlan?: (board: string, classVal: string, subject: string) => void;
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
}) {
  return (
    <CurriculumPlanner
      initialBoard={initialBoard}
      initialClass={initialClass}
      initialSubject={initialSubject}
      onBackToKnowledgeMap={onBackToKnowledgeMap}
      onProceedToStudyPlan={onProceedToStudyPlan}
    />
  );
}

function StudyPlanWrapper({
  initialBoard,
  initialClass,
  initialSubject,
  onProceedToGuidelines,
}: {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onProceedToGuidelines: (board: string, classVal: string, subject: string) => void;
}) {
  return (
    <WeekWiseStudyPlan
      initialBoard={initialBoard}
      initialClass={initialClass}
      initialSubject={initialSubject}
      onProceedToGuidelines={onProceedToGuidelines}
    />
  );
}

function TeacherGuidelinesWrapper({
  initialBoard,
  initialClass,
  initialSubject,
  onProceedToClassroom,
  onNavigateToStudyPlan,
  onNavigateToSyllabus,
}: {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onProceedToClassroom: (board: string, classVal: string, subject: string) => void;
  onNavigateToStudyPlan: (board: string, classVal: string, subject: string) => void;
  onNavigateToSyllabus: (board: string, classVal: string, subject: string) => void;
}) {
  return (
    <TeacherGuidelines
      initialBoard={initialBoard}
      initialClass={initialClass}
      initialSubject={initialSubject}
      onProceedToClassroom={onProceedToClassroom}
      onNavigateToStudyPlan={onNavigateToStudyPlan}
      onNavigateToSyllabus={onNavigateToSyllabus}
    />
  );
}

function ClassroomWrapper({
  initialBoard,
  initialClass,
  initialSubject,
}: {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
}) {
  return (
    <InteractiveClassroom
      initialBoard={initialBoard}
      initialClass={initialClass}
      initialSubject={initialSubject}
    />
  );
}

// ─── Inner dashboard (has access to RoleContext) ─────────────────────────────
function DashboardInner() {
  const [selectedMenu, setSelectedMenu] = useState('unitwisesyllabus');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navigationState, setNavigationState] = useState<NavigationState | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const actions = ConstantActions();


const handleLogout = () => {
  actions.logout();
};

  // Navigation handlers
  const handleNavigateToCurriculum = (board: string, classVal: string, subject: string) => {
    setNavigationState({ board, class: classVal, subject });
    setSelectedMenu('curriculum');
    if (isMobile) setMobileOpen(false);
  };

  const handleBackToKnowledgeMap = (board: string, classVal: string, subject: string) => {
    setNavigationState({ board, class: classVal, subject });
    setSelectedMenu('unitwisesyllabus');
    if (isMobile) setMobileOpen(false);
  };

  const handleNavigateToStudyPlan = (board: string, classVal: string, subject: string) => {
    setNavigationState({ board, class: classVal, subject });
    setSelectedMenu('studyPlan');
    if (isMobile) setMobileOpen(false);
  };

  const handleNavigateToTeacherGuidelines = (board: string, classVal: string, subject: string) => {
    setNavigationState({ board, class: classVal, subject });
    setSelectedMenu('teacherGuidelines');
    if (isMobile) setMobileOpen(false);
  };

  const handleNavigateToClassroom = (board: string, classVal: string, subject: string) => {
    setNavigationState({ board, class: classVal, subject });
    setSelectedMenu('classroom');
    if (isMobile) setMobileOpen(false);
  };

  // Determine which component to render with props
  const renderComponent = () => {
    switch (selectedMenu) {
      case 'unitwisesyllabus':
        return (
          <KnowledgeMapWrapper
            onNavigateToCurriculum={handleNavigateToCurriculum}
            initialBoard={navigationState?.board}
            initialClass={navigationState?.class}
            initialSubject={navigationState?.subject}
          />
        );
      case 'curriculum':
        return (
          <CurriculumWrapper
            onBackToKnowledgeMap={handleBackToKnowledgeMap}
            onProceedToStudyPlan={handleNavigateToStudyPlan}
            initialBoard={navigationState?.board}
            initialClass={navigationState?.class}
            initialSubject={navigationState?.subject}
          />
        );
      case 'studyPlan':
        return (
          <StudyPlanWrapper
            initialBoard={navigationState?.board}
            initialClass={navigationState?.class}
            initialSubject={navigationState?.subject}
            onProceedToGuidelines={handleNavigateToTeacherGuidelines}
          />
        );
      case 'teacherGuidelines':
        return (
          <TeacherGuidelinesWrapper
            initialBoard={navigationState?.board}
            initialClass={navigationState?.class}
            initialSubject={navigationState?.subject}
            onProceedToClassroom={handleNavigateToClassroom}
            onNavigateToStudyPlan={handleNavigateToStudyPlan}
            onNavigateToSyllabus={handleBackToKnowledgeMap}
          />
        );
      case 'classroom':
        return (
          <ClassroomWrapper
            initialBoard={navigationState?.board}
            initialClass={navigationState?.class}
            initialSubject={navigationState?.subject}
          />
        );
      default: {
        const MenuItem = menuItems.find(item => item.id === selectedMenu)?.component;
        return MenuItem ? <MenuItem /> : null;
      }
    }
  };

  const selectedItem = menuItems.find(item => item.id === selectedMenu);

  // ── Sidebar content ──────────────────────────────────────────────────────────
  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #E0E0E0',
          bgcolor: '#1565C0',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <Avatar sx={{ bgcolor: '#FFC107', width: 38, height: 38, mr: 1 }}>
          <SchoolIcon sx={{ fontSize: 20, color: '#1565C0' }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
          Platform Flow
        </Typography>
      </Box>

      {/* <RoleSwitcherInline compact={false} /> */}

      <List sx={{ mt: 1, px: 1, flexGrow: 1, overflowY: 'auto' }}>
        {menuItems.map((item) => {
          const isSelected = selectedMenu === item.id;
          return (
            <ListItem
              component="button"
              key={item.id}
              selected={isSelected}
              onClick={() => {
                setSelectedMenu(item.id);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.1,
                cursor: 'pointer',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                bgcolor: 'transparent',
                '&.Mui-selected': {
                  bgcolor: '#E3F2FD',
                  '&:hover': { bgcolor: '#E3F2FD' },
                  '& .MuiListItemIcon-root': { color: '#1565C0' },
                },
                '&:hover': { bgcolor: '#F5F5F5' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: isSelected ? '#1565C0' : '#757575' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.fullLabel}
                primaryTypographyProps={{
                  fontSize: 13,
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? '#1565C0' : '#333',
                }}
              />
            </ListItem>
          );
        })}
      </List>

      <Divider />

      <List sx={{ px: 1, flexShrink: 0 }}>
        <ListItem
          component="button"
          sx={{
            borderRadius: 2,
            mb: 0.5,
            py: 1.1,
            cursor: 'pointer',
            border: 'none',
            width: '100%',
            textAlign: 'left',
            bgcolor: 'transparent',
            '&:hover': { bgcolor: '#F5F5F5' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: '#757575' }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: 13, color: '#333' }} />
        </ListItem>
      </List>

      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <CurrentUserAvatar />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CurrentUserInfo />
        </Box>
      </Box>
    </Box>
  );

  // Component to show current user info with role
  function CurrentUserAvatar() {
    const { role } = useRole();
    return (
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: role === 'coordinator' ? '#1B5E20' : '#1565C0',
          transition: 'background-color .2s',
        }}
      >
        {role === 'coordinator'
          ? <CoordIcon sx={{ fontSize: 16 }} />
          : <TeacherIcon sx={{ fontSize: 16 }} />}
      </Avatar>
    );
  }

  function CurrentUserInfo() {
    const { role } = useRole();

    const handleLogout = () => {
      // Your logout logic
      localStorage.clear();
      sessionStorage.clear();

      // If using react-router
      window.location.href = '/login';
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
            {role === 'coordinator' ? 'Academic Coordinator' : 'Teacher User'}
          </Typography>

          <IconButton size="small" onClick={handleLogout}>
            <LogoutIcon
              sx={{
                fontSize: 18,
                color: '#757575',
                '&:hover': {
                  color: '#d32f2f',
                },
              }}
            />
          </IconButton>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: '#757575',
            fontSize: 10,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {role === 'coordinator'
            ? 'coordinator@school.edu'
            : 'teacher@school.edu'}
        </Typography>
      </>
    );
  }
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F5F7FA' }}>

      {/* ── DESKTOP: Permanent sidebar ── */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#FFFFFF',
              borderRight: '1px solid #E0E0E0',
              height: '100vh',
              overflowY: 'hidden',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* ── MOBILE: Swipeable drawer ── */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          onOpen={() => setMobileOpen(true)}
          disableBackdropTransition
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              bgcolor: '#FFFFFF',
            },
          }}
        >
          {sidebarContent}
        </SwipeableDrawer>
      )}

      {/* ── Main content area ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: '#F5F7FA',
          pb: isMobile ? '64px' : 0,
        }}
      >
        {/* Mobile top AppBar */}
        {isMobile && (
          <AppBar
            position="sticky"
            elevation={1}
            sx={{ bgcolor: '#1565C0', zIndex: theme.zIndex.drawer - 1 }}
          >
            <Toolbar sx={{ minHeight: '52px !important', gap: 1 }}>
              <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
              <Avatar sx={{ bgcolor: '#FFC107', width: 26, height: 26 }}>
                <SchoolIcon sx={{ fontSize: 14, color: '#1565C0' }} />
              </Avatar>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, flexGrow: 1, fontSize: '0.9rem', ml: 0.75 }}
              >
                {selectedItem?.fullLabel ?? 'Platform Flow'}
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Page content */}
        <Box sx={{ flexGrow: 1, p: isMobile ? 1.5 : 0, overflowY: 'auto' }}>
          {renderComponent()}
        </Box>
      </Box>

      {/* ── MOBILE: Bottom navigation ── */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: '1px solid #E0E0E0',
          }}
          elevation={3}
        >
          <BottomNavigation
            value={selectedMenu}
            onChange={(_, newValue) => setSelectedMenu(newValue)}
            showLabels
            sx={{ height: 64 }}
          >
            {menuItems.map((item) => (
              <BottomNavigationAction
                key={item.id}
                label={item.label}
                value={item.id}
                icon={item.icon}
                sx={{
                  minWidth: 0,
                  px: 0.5,
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.6rem',
                    '&.Mui-selected': { fontSize: '0.65rem' },
                  },
                  '&.Mui-selected': { color: '#1565C0' },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}

// ── Root export: wraps DashboardInner in RoleProvider ────────────────────────
export default function MainDashboard() {
  return (
    <RoleProvider>
      <DashboardInner />
    </RoleProvider>
  );
}